import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';

import { useDescuentoTrasladoFibra, SLUG_DESCUENTO_TRASLADO_FIBRA, DEFAULT_DESCUENTO_TRASLADO_FIBRA } from '@/shared/hooks/useDescuentoTrasladoFibra';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import {
  QK_CATALOGO_VISITAS_TRASLADOS,
  guardarCatalogoVisitasTraslados,
} from '@/shared/actions/erp/administration/catalogo-visitas-traslados.actions';
import { useFetchProductoSimple } from '@/shared/actions/erp';
import {
  SLUG_PRECIOS_CONCEPTOS_VISITA,
  DEFAULT_IVA_PORCENTAJE,
  FLUJOS_CONCEPTO,
  FLUJO_LABEL,
  calcularPrecio,
  usePreciosConceptosVisita,
} from '@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/usePreciosConceptosVisita';
import type {
  FlujoConcepto,
  ProductoConcepto,
} from '@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/usePreciosConceptosVisita';
import {
  SLUG_TIPOS_INSTALACION_VISITA,
  useTiposInstalacion,
} from '@/erp/orden-servicio/orden-servicio-visita/components/tipo-instalacion/useTiposInstalacion';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { hasSystemModule } from '@/shared';
import { SystemModulesEnum } from '@/auth/interfaces/system-modules.enum';
import { useAuthStore } from '@/store/auth';

const ACCENT = '#1565c0';

/** Fila editable del catálogo de conceptos (concepto + productos[] + precio). */
type FilaConcepto = {
  concepto: string;
  valor_sin_iva: string;
  liga_producto: boolean;
  permite_punto_red: boolean;
  productos: ProductoConcepto[];
  /** Flujos donde el concepto está disponible (visitas / traslados). */
  aplica_a: FlujoConcepto[];
};

/** Encabezado de sección con icono + título + descripción. */
function SeccionHeader({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc?: string;
}) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2 }}>
      <Box sx={{ color: ACCENT, display: 'flex', mt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography variant="subtitle1" fontWeight={800}>
          {title}
        </Typography>
        {desc && (
          <Typography variant="body2" color="text.secondary">
            {desc}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/**
 * Selector MÚLTIPLE de productos de inventario (búsqueda global por descripción).
 * Un concepto puede ligar uno o varios productos; el técnico elige cuál entregó.
 */
function ProductosMultiPicker({
  value,
  onChange,
  disabled,
}: {
  value: ProductoConcepto[];
  onChange: (p: ProductoConcepto[]) => void;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useFetchProductoSimple({
    enabled: search.trim().length >= 2,
    params: { descripcion: search.toUpperCase(), es_para_venta: true, page_size: 20 } as any,
  });
  const options: any[] = (data as any)?.data?.data || [];
  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      loading={isLoading}
      value={value as any[]}
      disabled={disabled}
      filterSelectedOptions
      getOptionLabel={(o: any) =>
        o ? `${o.codigo || ''}${o.descripcion ? ' · ' + o.descripcion : ''}` : ''
      }
      isOptionEqualToValue={(o: any, v: any) => o?.codigo === v?.codigo}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') setSearch(v);
      }}
      onChange={(_, arr: any[]) =>
        onChange(
          (arr || []).map((o) => ({
            codigo: o.codigo,
            id_producto: o.id_producto,
            descripcion: o.descripcion,
          }))
        )
      }
      renderInput={(params) => (
        <TextField {...params} label="Productos de inventario" placeholder="Buscar y agregar…" />
      )}
    />
  );
}

/**
 * Chips "Aplica a: Visitas / Traslados" de un concepto. El flujo de la pestaña
 * actual siempre queda marcado (si se desmarca, el concepto desaparecería de
 * esta pestaña; para eso está el botón "Quitar de …").
 */
function AplicaAChips({
  value,
  flujoActual,
  onChange,
}: {
  value: FlujoConcepto[];
  flujoActual: FlujoConcepto;
  onChange: (next: FlujoConcepto[]) => void;
}) {
  const toggle = (f: FlujoConcepto) => {
    if (f === flujoActual) return;
    const next = value.includes(f) ? value.filter((x) => x !== f) : [...value, f];
    onChange(next.length ? next : [flujoActual]);
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="caption" color="text.secondary">
        Aplica a
      </Typography>
      {FLUJOS_CONCEPTO.map((f) => {
        const activo = value.includes(f);
        const fijo = f === flujoActual;
        return (
          <Tooltip
            key={f}
            title={
              fijo
                ? `Este concepto pertenece a ${FLUJO_LABEL[f]} (pestaña actual)`
                : activo
                ? `Quitar de ${FLUJO_LABEL[f]}`
                : `Habilitar también en ${FLUJO_LABEL[f]}`
            }
          >
            <Chip
              size="small"
              label={FLUJO_LABEL[f]}
              color={activo ? 'primary' : 'default'}
              variant={activo ? 'filled' : 'outlined'}
              onClick={fijo ? undefined : () => toggle(f)}
              sx={{ fontWeight: 600, cursor: fijo ? 'default' : 'pointer' }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

/**
 * Panel "Administración" del catálogo de tickets. Expone:
 *  - Sub-tab Traslados: "Descuento por traslado FibraPoints" + el catálogo de
 *    conceptos / puntos de red / métodos de pago LIGADOS a traslados.
 *  - Sub-tab Visitas: el mismo catálogo ligado a visitas + tipos de instalación.
 *
 * El catálogo de conceptos es UNO solo (Parametro `precios_conceptos_visita`):
 * cada concepto tiene `aplica_a` (visitas y/o traslados) y se administra desde
 * cualquiera de las dos pestañas; los métodos de pago y la config de puntos de
 * red (precio metro UTP + metros libres) son INDEPENDIENTES por flujo. El IVA es
 * global. Guardar desde cualquier pestaña persiste el catálogo completo.
 */
export default function PanelAdministracion() {
  // ---------------- Descuento FibraPoints ----------------
  const { descuento, isLoading } = useDescuentoTrasladoFibra();
  const [valor, setValor] = useState<string>('');
  // El catálogo ahora vive en tablas: se guarda por el endpoint
  // catalogo-visitas-traslados y se invalida su query para que los hooks
  // relean lo que de verdad quedó en base.
  const queryClient = useQueryClient();
  const [savingDescuento, setSavingDescuento] = useState(false);

  useEffect(() => {
    if (!isLoading) setValor(String(descuento ?? ''));
  }, [isLoading, descuento]);

  const dirty = String(valor) !== String(descuento ?? '');
  const invalido = valor === '' || !Number.isFinite(Number(valor)) || Number(valor) < 0;
  const puedeGuardarDescuento =
    !isLoading && !invalido && !savingDescuento && dirty;

  const handleSave = async () => {
    if (invalido) return;
    setSavingDescuento(true);
    try {
      await guardarCatalogoVisitasTraslados({
        descuento_traslado_fibrapoints: Number(valor),
      });
      await queryClient.invalidateQueries({ queryKey: QK_CATALOGO_VISITAS_TRASLADOS });
      toast.success('Descuento actualizado correctamente');
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo guardar el descuento.');
    } finally {
      setSavingDescuento(false);
    }
  };

  // ---------------- Catálogo de conceptos (visitas + traslados) ----------------
  const {
    conceptosTodos,
    ivaPorcentaje,
    puntoRedVisita,
    puntoRedTraslado,
    metodosPagoVisita,
    metodosPagoTraslado,
    excedenteFibraTraslado,
    parametro: paramPrecios,
    isLoading: loadingConceptos,
  } = usePreciosConceptosVisita();
  const [ivaPct, setIvaPct] = useState<string>(String(DEFAULT_IVA_PORCENTAJE));
  const [filas, setFilas] = useState<FilaConcepto[]>([]);
  // Config "Puntos de Red" por flujo: precio por metro de cable UTP + metros libres.
  const [puntoRedForm, setPuntoRedForm] = useState<
    Record<FlujoConcepto, { precio_metro: string; metros_libres: string }>
  >({
    VISITA: { precio_metro: '', metros_libres: '' },
    TRASLADO: { precio_metro: '', metros_libres: '' },
  });
  // Métodos de pago por flujo (parametrizables: se pueden borrar/agregar).
  const [metodosForm, setMetodosForm] = useState<Record<FlujoConcepto, string[]>>({
    VISITA: [],
    TRASLADO: [],
  });
  // Excedente de fibra (solo traslados): metros gratis + costo por metro excedente.
  const [excedenteFibraForm, setExcedenteFibraForm] = useState<{ metros_gratis: string; precio_metro: string }>({
    metros_gratis: '',
    precio_metro: '',
  });

  useEffect(() => {
    if (loadingConceptos) return;
    setIvaPct(String(ivaPorcentaje));
    setFilas(
      conceptosTodos.map((c) => ({
        concepto: c.concepto,
        valor_sin_iva: String(c.valor_sin_iva ?? 0),
        liga_producto: !!c.liga_producto,
        permite_punto_red: !!c.permite_punto_red,
        productos: Array.isArray(c.productos) ? c.productos : [],
        aplica_a: Array.isArray(c.aplica_a) && c.aplica_a.length ? c.aplica_a : ['VISITA'],
      }))
    );
    setPuntoRedForm({
      VISITA: {
        precio_metro: String(puntoRedVisita.precio_metro_sin_iva),
        metros_libres: String(puntoRedVisita.metros_libres),
      },
      TRASLADO: {
        precio_metro: String(puntoRedTraslado.precio_metro_sin_iva),
        metros_libres: String(puntoRedTraslado.metros_libres),
      },
    });
    setMetodosForm({
      VISITA: Array.isArray(metodosPagoVisita) ? metodosPagoVisita : [],
      TRASLADO: Array.isArray(metodosPagoTraslado) ? metodosPagoTraslado : [],
    });
    setExcedenteFibraForm({
      metros_gratis: String(excedenteFibraTraslado.metros_gratis),
      precio_metro: String(excedenteFibraTraslado.precio_metro_sin_iva),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingConceptos,
    JSON.stringify(conceptosTodos),
    ivaPorcentaje,
    JSON.stringify(puntoRedVisita),
    JSON.stringify(puntoRedTraslado),
    JSON.stringify(metodosPagoVisita),
    JSON.stringify(metodosPagoTraslado),
    JSON.stringify(excedenteFibraTraslado),
  ]);

  const [savingConceptos, setSavingConceptos] = useState(false);

  const setFila = (idx: number, patch: Partial<FilaConcepto>) =>
    setFilas((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const addFila = (flujo: FlujoConcepto) =>
    setFilas((prev) => [
      ...prev,
      { concepto: '', valor_sin_iva: '0', liga_producto: true, permite_punto_red: false, productos: [], aplica_a: [flujo] },
    ]);
  /**
   * Quitar un concepto desde una pestaña: si también aplica al otro flujo, solo
   * se desliga de ESTE flujo (sigue existiendo para el otro); si era exclusivo,
   * se elimina del catálogo.
   */
  const removeFila = (idx: number, flujo: FlujoConcepto) =>
    setFilas((prev) =>
      prev
        .map((f, i) => {
          if (i !== idx) return f;
          const otros = f.aplica_a.filter((x) => x !== flujo);
          return otros.length ? { ...f, aplica_a: otros } : null;
        })
        .filter((f): f is FilaConcepto => f !== null)
    );

  const setMetodoPago = (flujo: FlujoConcepto, idx: number, val: string) =>
    setMetodosForm((prev) => ({ ...prev, [flujo]: prev[flujo].map((m, i) => (i === idx ? val : m)) }));
  const addMetodoPago = (flujo: FlujoConcepto) =>
    setMetodosForm((prev) => ({ ...prev, [flujo]: [...prev[flujo], ''] }));
  const removeMetodoPago = (flujo: FlujoConcepto, idx: number) =>
    setMetodosForm((prev) => ({ ...prev, [flujo]: prev[flujo].filter((_, i) => i !== idx) }));
  const setPuntoRedCampo = (
    flujo: FlujoConcepto,
    campo: 'precio_metro' | 'metros_libres',
    val: string
  ) => setPuntoRedForm((prev) => ({ ...prev, [flujo]: { ...prev[flujo], [campo]: val } }));

  const pctNum = Number(ivaPct);
  const pctValido = Number.isFinite(pctNum) && pctNum >= 0;

  const handleSaveConceptos = async () => {
    const ivaFinal = pctValido ? pctNum : DEFAULT_IVA_PORCENTAJE;
    const conceptosJson = filas
      .filter((f) => (f.concepto || '').trim() !== '')
      .map((f) => {
        const prods = f.liga_producto
          ? (f.productos || []).map((p) => ({
              codigo: p.codigo || undefined,
              id_producto: p.id_producto || undefined,
              descripcion: p.descripcion || undefined,
            }))
          : [];
        return {
          concepto: f.concepto.trim(),
          valor_sin_iva: Number(f.valor_sin_iva) >= 0 ? Number(f.valor_sin_iva) || 0 : 0,
          liga_producto: !!f.liga_producto,
          permite_punto_red: !!f.permite_punto_red,
          productos: prods,
          aplica_a: f.aplica_a.length ? f.aplica_a : ['VISITA'],
        };
      });
    const puntoRedJson = (flujo: FlujoConcepto) => ({
      precio_metro_sin_iva:
        Number(puntoRedForm[flujo].precio_metro) >= 0 ? Number(puntoRedForm[flujo].precio_metro) || 0 : 0,
      metros_libres:
        Number(puntoRedForm[flujo].metros_libres) >= 0 ? Number(puntoRedForm[flujo].metros_libres) || 0 : 0,
    });
    const metodosJson = (flujo: FlujoConcepto) =>
      metodosForm[flujo]
        .map((m) => (m || '').trim())
        .filter((m, i, a) => m !== '' && a.indexOf(m) === i);
    const payload = {
      iva_porcentaje: ivaFinal,
      conceptos: conceptosJson,
      punto_red: puntoRedJson('VISITA'),
      punto_red_traslado: puntoRedJson('TRASLADO'),
      metodos_pago: metodosJson('VISITA'),
      metodos_pago_traslado: metodosJson('TRASLADO'),
      // Excedente de fibra de traslados (se guarda con el resto del catálogo).
      excedente_fibra_traslado: {
        metros_gratis:
          Number(excedenteFibraForm.metros_gratis) >= 0 ? Number(excedenteFibraForm.metros_gratis) || 0 : 0,
        precio_metro_sin_iva:
          Number(excedenteFibraForm.precio_metro) >= 0 ? Number(excedenteFibraForm.precio_metro) || 0 : 0,
      },
    };
    setSavingConceptos(true);
    try {
      const res = await guardarCatalogoVisitasTraslados({ precios_conceptos: payload as any });
      await queryClient.invalidateQueries({ queryKey: QK_CATALOGO_VISITAS_TRASLADOS });
      toast.success('Catálogo de conceptos actualizado');
      // El backend avisa, por ejemplo, de productos ligados que ya no existen
      // en inventario (en tablas no se pueden referenciar en el aire).
      (res?.advertencias || []).forEach((a) => toast.warn(a, { autoClose: 9000 }));
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo guardar el catálogo de conceptos.');
    } finally {
      setSavingConceptos(false);
    }
  };

  // ---------------- Tipos de instalación (árbol) ----------------
  const {
    tipos: tiposCatalogo,
    parametro: paramTipos,
    isLoading: loadingTipos,
  } = useTiposInstalacion();
  type FilaSubtipo = { nombre: string; rango: string };
  type FilaTipo = { nombre: string; es_default: boolean; subtipos: FilaSubtipo[] };
  const [filasTipos, setFilasTipos] = useState<FilaTipo[]>([]);

  useEffect(() => {
    if (loadingTipos) return;
    setFilasTipos(
      tiposCatalogo.map((t) => ({
        nombre: t.nombre,
        es_default: !!t.es_default,
        subtipos: (t.subtipos || []).map((s) => ({ nombre: s.nombre, rango: s.rango || '' })),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTipos, JSON.stringify(tiposCatalogo)]);

  const [savingTipos, setSavingTipos] = useState(false);

  const setTipo = (idx: number, patch: Partial<FilaTipo>) =>
    setFilasTipos((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  const addTipo = () =>
    setFilasTipos((prev) => [...prev, { nombre: '', es_default: false, subtipos: [] }]);
  const removeTipo = (idx: number) =>
    setFilasTipos((prev) => prev.filter((_, i) => i !== idx));
  const setDefaultTipo = (idx: number) =>
    setFilasTipos((prev) => prev.map((t, i) => ({ ...t, es_default: i === idx })));
  const addSubtipo = (idx: number) =>
    setFilasTipos((prev) => prev.map((t, i) => (i === idx ? { ...t, subtipos: [...t.subtipos, { nombre: '', rango: '' }] } : t)));
  const setSubtipo = (idx: number, sIdx: number, patch: Partial<FilaSubtipo>) =>
    setFilasTipos((prev) => prev.map((t, i) => (i === idx ? { ...t, subtipos: t.subtipos.map((s, j) => (j === sIdx ? { ...s, ...patch } : s)) } : t)));
  const removeSubtipo = (idx: number, sIdx: number) =>
    setFilasTipos((prev) => prev.map((t, i) => (i === idx ? { ...t, subtipos: t.subtipos.filter((_, j) => j !== sIdx) } : t)));

  const handleSaveTipos = async () => {
    const tiposJson = filasTipos
      .filter((t) => (t.nombre || '').trim() !== '')
      .map((t) => ({
        nombre: t.nombre.trim(),
        es_default: !!t.es_default,
        subtipos: t.subtipos
          .filter((s) => (s.nombre || '').trim() !== '')
          .map((s) => ({ nombre: s.nombre.trim(), rango: s.rango.trim() || undefined })),
      }));
    setSavingTipos(true);
    try {
      await guardarCatalogoVisitasTraslados({ tipos_instalacion: { tipos: tiposJson } as any });
      await queryClient.invalidateQueries({ queryKey: QK_CATALOGO_VISITAS_TRASLADOS });
      toast.success('Tipos de instalación actualizados');
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo guardar los tipos de instalación.');
    } finally {
      setSavingTipos(false);
    }
  };

  const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

  const cardSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
  } as const;

  // --- Sub-tabs "Visitas" / "Traslados" gateados por módulo de sistema ---
  // Se gestionan en Gestión de Accesos (TICKETS_CATALOGOS.ADMINISTRACION_VISITAS /
  // ..._TRASLADOS). Retrocompat: si el usuario NO tiene ninguno de los dos
  // sub-módulos nuevos, se le muestran ambos (para no romper accesos que solo
  // tenían el módulo paraguas ADMINISTRACION). El superusuario siempre ve todo.
  const authUser = useAuthStore((s) => s.user);
  const isSuperUser = !!(authUser as any)?.is_superuser;
  const tieneModVisitas = hasSystemModule(SystemModulesEnum.CATALOGOS_ADMINISTRACION_VISITAS);
  const tieneModTraslados = hasSystemModule(SystemModulesEnum.CATALOGOS_ADMINISTRACION_TRASLADOS);
  const sinSubModulos = !tieneModVisitas && !tieneModTraslados;
  const verVisitas = isSuperUser || tieneModVisitas || sinSubModulos;
  const verTraslados = isSuperUser || tieneModTraslados || sinSubModulos;

  const visibleSubTabs = (
    [
      verVisitas && { key: 'visitas' as const, label: 'Visitas' },
      verTraslados && { key: 'traslados' as const, label: 'Traslados' },
    ] as ({ key: 'visitas' | 'traslados'; label: string } | false)[]
  ).filter(Boolean) as { key: 'visitas' | 'traslados'; label: string }[];

  const [subTab, setSubTab] = useState(0);
  const activeIndex = Math.min(subTab, Math.max(0, visibleSubTabs.length - 1));
  const activeKey = visibleSubTabs[activeIndex]?.key;

  if (visibleSubTabs.length === 0) {
    return (
      <Box sx={{ maxWidth: 1080 }}>
        <Typography variant="h6" fontWeight={800}>
          Administración
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No tienes acceso a ninguna sección de Administración.
        </Typography>
      </Box>
    );
  }

  /** Sección "Conceptos" del flujo (IVA global + cards de los conceptos ligados). */
  const renderConceptos = (flujo: FlujoConcepto) => {
    const nombreFlujo = FLUJO_LABEL[flujo].toLowerCase();
    const visibles = filas.filter((f) => f.aplica_a.includes(flujo));
    return (
      <Paper elevation={0} sx={cardSx}>
        <SeccionHeader
          icon={<Inventory2OutlinedIcon />}
          title={`Conceptos de ${nombreFlujo} (Otros conceptos)`}
          desc={`Cada concepto puede ligar uno o varios productos de inventario; el técnico elige cuál entregó y su serie. Se envía el VALOR TOTAL (con IVA) a Softnet. Con "Aplica a" un mismo concepto puede usarse también en ${flujo === 'VISITA' ? 'traslados' : 'visitas'}.`}
        />

        {/* IVA global (compartido por visitas y traslados) */}
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 1.5, mb: 2.5, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}
        >
          <PercentOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
          <Typography variant="body2" fontWeight={600}>
            IVA aplicado a todos los conceptos (visitas y traslados)
          </Typography>
          <TextField
            size="small"
            type="number"
            value={ivaPct}
            onChange={(e) => setIvaPct(e.target.value)}
            inputProps={{ step: 0.01, min: 0 }}
            disabled={loadingConceptos}
            error={!loadingConceptos && !pctValido}
            sx={{ width: 110 }}
            InputProps={{ endAdornment: <Typography variant="body2" color="text.secondary">%</Typography> }}
          />
          {!loadingConceptos && !pctValido && (
            <Typography variant="caption" color="error.main">
              Porcentaje inválido
            </Typography>
          )}
        </Paper>

        {visibles.length === 0 && !loadingConceptos && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Aún no hay conceptos ligados a {nombreFlujo}. Agrega uno nuevo o, desde la otra
            pestaña, marca "Aplica a → {FLUJO_LABEL[flujo]}" en un concepto existente.
          </Typography>
        )}

        {/* Cards por concepto (se itera el catálogo COMPLETO para conservar el
            índice real de cada fila; se ocultan las que no aplican al flujo). */}
        <Grid container spacing={2}>
          {filas.map((f, idx) => {
            if (!f.aplica_a.includes(flujo)) return null;
            const calc = calcularPrecio(Number(f.valor_sin_iva) || 0, pctValido ? pctNum : 0);
            const compartido = f.aplica_a.length > 1;
            return (
              <Grid key={idx} item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Concepto"
                      value={f.concepto}
                      placeholder="Nombre del concepto"
                      onChange={(e) => setFila(idx, { concepto: e.target.value })}
                    />
                    <Tooltip
                      title={
                        compartido
                          ? `Quitar de ${nombreFlujo} (se conserva para el otro flujo)`
                          : 'Quitar concepto del catálogo'
                      }
                    >
                      <IconButton size="small" color="error" onClick={() => removeFila(idx, flujo)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Grid item xs={7}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Valor sin IVA"
                        value={f.valor_sin_iva}
                        onChange={(e) => setFila(idx, { valor_sin_iva: e.target.value })}
                        inputProps={{ step: 0.01, min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary">
                          Total (con IVA)
                        </Typography>
                        <Chip
                          label={money(calc.valor_total)}
                          sx={{ fontWeight: 700, bgcolor: 'rgba(21,101,192,0.1)', color: ACCENT, width: 'fit-content' }}
                          size="small"
                        />
                      </Stack>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1.25 }} />

                  <AplicaAChips
                    value={f.aplica_a}
                    flujoActual={flujo}
                    onChange={(next) => setFila(idx, { aplica_a: next })}
                  />

                  <Divider sx={{ my: 1.25 }} />

                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={f.liga_producto}
                        onChange={(e) => setFila(idx, { liga_producto: e.target.checked })}
                      />
                    }
                    label={<Typography variant="body2">Liga producto(s) de inventario</Typography>}
                  />
                  {f.liga_producto ? (
                    <ProductosMultiPicker
                      value={f.productos || []}
                      onChange={(prods) => setFila(idx, { productos: prods })}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 0.5 }}>
                      Sin producto: no se elige serie ni descuenta stock.
                    </Typography>
                  )}

                  <FormControlLabel
                    sx={{ mt: 0.5 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!!f.permite_punto_red}
                        onChange={(e) => setFila(idx, { permite_punto_red: e.target.checked })}
                      />
                    }
                    label={<Typography variant="body2">Permite punto de red</Typography>}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={() => addFila(flujo)}
          sx={{ mt: 2 }}
        >
          Agregar concepto
        </Button>
      </Paper>
    );
  };

  /** Sección "Puntos de Red" (cable UTP) del flujo: precio por metro + metros libres. */
  const renderPuntosRed = (flujo: FlujoConcepto) => (
    <Paper elevation={0} sx={cardSx}>
      <SeccionHeader
        icon={<LanOutlinedIcon />}
        title={`Puntos de Red (cable UTP) — ${FLUJO_LABEL[flujo]}`}
        desc="Los primeros metros libres no se cobran; el excedente se cobra por metro o fracción (+ IVA). Ej.: 17.5 m con 15 libres → 3 m × precio. Configuración independiente por flujo."
      />
      <Grid container spacing={2} sx={{ maxWidth: 520 }}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Precio por metro (sin IVA)"
            value={puntoRedForm[flujo].precio_metro}
            onChange={(e) => setPuntoRedCampo(flujo, 'precio_metro', e.target.value)}
            inputProps={{ step: 0.01, min: 0 }}
            disabled={loadingConceptos}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Metros libres (sin cobro)"
            value={puntoRedForm[flujo].metros_libres}
            onChange={(e) => setPuntoRedCampo(flujo, 'metros_libres', e.target.value)}
            inputProps={{ step: 1, min: 0 }}
            disabled={loadingConceptos}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  /**
   * Sección "Excedente de fibra" (solo traslados): metros gratis + costo por
   * metro excedente. Con costo 0, Activaciones define el precio al aprobar.
   */
  const renderExcedenteFibra = () => {
    const costo = Number(excedenteFibraForm.precio_metro) || 0;
    const gratis = Number(excedenteFibraForm.metros_gratis) || 0;
    return (
      <Paper elevation={0} sx={cardSx}>
        <SeccionHeader
          icon={<CableOutlinedIcon />}
          title="Excedente de fibra — Traslados"
          desc="Aparece al técnico cuando agrega FIBRA PLANA en los materiales del traslado. Los primeros metros gratis no se cobran; lo que los supere se cobra por metro o fracción (+ IVA) y debe aprobarlo Activaciones, con su forma de pago, antes de que el técnico pueda finalizar."
        />
        <Grid container spacing={2} sx={{ maxWidth: 520 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Metros gratis (sin cobro)"
              value={excedenteFibraForm.metros_gratis}
              onChange={(e) => setExcedenteFibraForm((prev) => ({ ...prev, metros_gratis: e.target.value }))}
              inputProps={{ step: 1, min: 0 }}
              disabled={loadingConceptos}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Costo por metro excedente (sin IVA)"
              value={excedenteFibraForm.precio_metro}
              onChange={(e) => setExcedenteFibraForm((prev) => ({ ...prev, precio_metro: e.target.value }))}
              inputProps={{ step: 0.01, min: 0 }}
              disabled={loadingConceptos}
            />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {costo > 0
            ? `Ej.: ${gratis + 50} m registrados → 50 m × ${costo.toFixed(2)} = ${(50 * costo).toFixed(2)} + IVA.`
            : 'Con costo $0 no se cobra nada automáticamente: Activaciones gestiona el costo y el precio del excedente al aprobarlo.'}
        </Typography>
      </Paper>
    );
  };

  /** Sección "Métodos de pago" del flujo (lista independiente por flujo). */
  const renderMetodosPago = (flujo: FlujoConcepto) => {
    const lista = metodosForm[flujo];
    const nombreFlujo = FLUJO_LABEL[flujo].toLowerCase();
    return (
      <Paper elevation={0} sx={cardSx}>
        <SeccionHeader
          icon={<PaymentsOutlinedIcon />}
          title={`Métodos de pago (${nombreFlujo})`}
          desc={`Métodos disponibles en el selector 'Método de pago' de Otros conceptos de ${nombreFlujo} (Técnico, Operaciones, Activaciones). Puedes borrarlos o agregar más.`}
        />
        <Stack spacing={1} sx={{ maxWidth: 520 }}>
          {lista.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Sin métodos de pago. Agrega al menos uno.
            </Typography>
          )}
          {lista.map((m, idx) => (
            <Stack key={idx} direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                size="small"
                label={`Método ${idx + 1}`}
                value={m}
                onChange={(e) => setMetodoPago(flujo, idx, e.target.value)}
                disabled={loadingConceptos}
              />
              <Tooltip title="Quitar método">
                <IconButton size="small" color="error" onClick={() => removeMetodoPago(flujo, idx)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
          <Box>
            <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => addMetodoPago(flujo)}>
              Agregar método de pago
            </Button>
          </Box>
        </Stack>
      </Paper>
    );
  };

  /** Barra sticky "Guardar conceptos" (persiste el catálogo completo). */
  const renderGuardar = () => (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        py: 2,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 1 }}>
        Guarda conceptos, IVA, puntos de red y métodos de pago de visitas y traslados.
      </Typography>
      <Button
        variant="contained"
        startIcon={<SaveOutlinedIcon />}
        onClick={handleSaveConceptos}
        disabled={loadingConceptos || savingConceptos || !pctValido}
      >
        {savingConceptos ? 'Guardando…' : 'Guardar conceptos'}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1080 }}>
      <Typography variant="h6" fontWeight={800}>
        Administración
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Parámetros de configuración de traslados y visitas.
      </Typography>

      <Tabs
        value={activeIndex}
        onChange={(_, v) => setSubTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {visibleSubTabs.map((t) => (
          <Tab key={t.key} label={t.label} sx={{ textTransform: 'none', fontWeight: 700 }} />
        ))}
      </Tabs>

      {activeKey === 'traslados' && (
        <Stack spacing={3}>
          {/* ===================== TRASLADOS: FibraPoints ===================== */}
          <Paper elevation={0} sx={cardSx}>
          <SeccionHeader
            icon={<LocalOfferOutlinedIcon />}
            title="Traslados"
            desc="Descuento aplicado al pagar un traslado con FibraPoints."
          />
          {/* Ancho completo (igual que el sub-tab Visitas): la fila ocupa las 12
              columnas del card para que al alternar entre sub-tabs no cambie el
              ancho. Input + botón + columna explicativa. */}
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={5}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label="Descuento por traslado (FibraPoints)"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                disabled={isLoading}
                error={!isLoading && invalido}
                helperText={
                  !isLoading && invalido
                    ? 'Ingresa un número válido (≥ 0)'
                    : 'FibraPoints a descontar al pagar un traslado con puntos.'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SaveOutlinedIcon />}
                onClick={handleSave}
                disabled={!puedeGuardarDescuento}
                sx={{ mt: 0.25 }}
              >
                {savingDescuento ? 'Guardando…' : 'Guardar'}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Cantidad de FibraPoints que se descuentan al cliente cuando paga un
                traslado con puntos (en lugar de un cobro monetario).
              </Typography>
            </Grid>
          </Grid>
          </Paper>

          {/* ===================== TRASLADOS: catálogo ligado ===================== */}
          {renderConceptos('TRASLADO')}
          {renderPuntosRed('TRASLADO')}
          {renderExcedenteFibra()}
          {renderMetodosPago('TRASLADO')}
          {renderGuardar()}
        </Stack>
      )}

      {activeKey === 'visitas' && (
        <Stack spacing={3}>
          {/* ===================== VISITAS: catálogo ligado ===================== */}
          {renderConceptos('VISITA')}
          {renderPuntosRed('VISITA')}

          {/* ===================== TIPOS DE INSTALACIÓN ===================== */}
          <Paper elevation={0} sx={cardSx}>
          <SeccionHeader
            icon={<AccountTreeOutlinedIcon />}
            title="Tipos de instalación (visitas)"
            desc="Árbol de tipos elegible en Crear Ticket, Operaciones, Activaciones y Técnico. 'Empaquetado' usa subtipos por rango de metros. Marca uno como predeterminado."
          />
          <Grid container spacing={2}>
            {filasTipos.map((t, idx) => (
              <Grid key={idx} item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Tipo"
                      value={t.nombre}
                      onChange={(e) => setTipo(idx, { nombre: e.target.value })}
                    />
                    <Tooltip title="Quitar tipo">
                      <IconButton size="small" color="error" onClick={() => removeTipo(idx)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <FormControlLabel
                    control={
                      <Checkbox size="small" checked={t.es_default} onChange={() => setDefaultTipo(idx)} />
                    }
                    label={<Typography variant="caption">Predeterminado</Typography>}
                  />

                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Subtipos (opcional; con rango de metros)
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 0.75 }}>
                    {t.subtipos.map((s, sIdx) => (
                      <Stack key={sIdx} direction="row" spacing={1} alignItems="center">
                        <TextField
                          size="small"
                          label="Subtipo"
                          value={s.nombre}
                          onChange={(e) => setSubtipo(idx, sIdx, { nombre: e.target.value })}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Rango"
                          placeholder="50m a 150m"
                          value={s.rango}
                          onChange={(e) => setSubtipo(idx, sIdx, { rango: e.target.value })}
                          sx={{ flex: 1 }}
                        />
                        <IconButton size="small" color="error" onClick={() => removeSubtipo(idx, sIdx)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                  <Button startIcon={<AddIcon />} size="small" onClick={() => addSubtipo(idx)} sx={{ mt: 1 }}>
                    Agregar subtipo
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addTipo}>
              Agregar tipo
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveTipos}
              disabled={loadingTipos || savingTipos}
            >
              {savingTipos ? 'Guardando…' : 'Guardar tipos de instalación'}
            </Button>
          </Stack>
          </Paper>

          {renderMetodosPago('VISITA')}
          {renderGuardar()}
        </Stack>
      )}
    </Box>
  );
}
